import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Card, Table, Tag, Button, Space, Typography, Tabs, Modal, Input, InputNumber, DatePicker, Upload,
    message, Select, Row, Col, Statistic, Empty, Popconfirm,
} from "antd";
import {
    PartitionOutlined, PlusOutlined, UploadOutlined, LockOutlined, SafetyCertificateOutlined,
    CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, EyeOutlined, UserAddOutlined,
    SolutionOutlined, ThunderboltOutlined, DeleteOutlined, FileTextOutlined, EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../variables/constants";
import { rightsService } from "../../services/rightsService";
import SideBar from "../../components/SideBar";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";

const { Text, Title } = Typography;
const PRIMARY = "#242166";
const BRANCHES = ["GUM", "VT", "TPV", "MOTO"];
const NATURES = [
    { code: "CORPOREL", label: "Corporel" },
    { code: "MATERIEL", label: "Matériel" },
    { code: "MIXTE", label: "Mixte" },
];
const TYPE = "SINISTRE"; // cet écran = Commission Technique SINISTRE

const STATUT = {
    EN_ATTENTE: { color: "orange", label: "En attente" },
    INSCRIT: { color: "blue", label: "Inscrit" },
    APPROUVE: { color: "green", label: "Approuvé" },
    REJETE: { color: "red", label: "Rejeté" },
    AJOURNE: { color: "gold", label: "Ajourné" },
    CLOTURE: { color: "default", label: "Clôturé" },
};
const SEANCE_STATUT = { PLANIFIEE: "blue", TENUE: "gold", CLOTUREE: "default" };
const fmt = (v) => (v || v === 0 ? Number(v).toLocaleString("fr-FR") : "-");
const isPool = () => (LOGGED_USER?.profil?.organisme?.typeOrganisme || "").toUpperCase() === "POOL";
const isMembre = () => isPool() || rightsService("HSI111");
const canConfig = () => isPool() || rightsService("HPA109");
const myOrgCode = () => LOGGED_USER?.profil?.organisme?.code;

const CommissionTechnique = () => {
    const navigate = useNavigate();
    const [dossiers, setDossiers] = useState([]);
    const [seances, setSeances] = useState([]);
    const [matrice, setMatrice] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [compagnies, setCompagnies] = useState([]);
    const [loading, setLoading] = useState(false);

    const charger = useCallback(() => {
        setLoading(true);
        Promise.all([
            axiosInstance.get("/api/commission-technique/dossiers").then((r) => setDossiers(r.data || [])).catch(() => {}),
            axiosInstance.get("/api/commission-technique/seances").then((r) => setSeances((r.data || []).filter((s) => (s.type || "SINISTRE") === TYPE))).catch(() => {}),
            axiosInstance.get("/api/commission-technique/seuils/matrice").then((r) => setMatrice(r.data || [])).catch(() => {}),
            axiosInstance.get("/api/commission-technique/participants", { params: { type: TYPE } }).then((r) => setParticipants(r.data || [])).catch(() => {}),
            axiosInstance.get("/api/organisme/assurance").then((r) => setCompagnies(r.data || [])).catch(() => {}),
        ]).finally(() => setLoading(false));
    }, []);
    useEffect(() => { charger(); }, [charger]);

    const ouvrirDossier = (d) => navigate(`/details-sinistre/v2?codeId=${d.sinistreCodeId}&tab=commission`);

    const statuer = (d, decision) => {
        let avis = "";
        Modal.confirm({
            title: `Décision : ${STATUT[decision]?.label || decision} — ${d.numeroSinistre || ""}`,
            content: <Input.TextArea rows={3} placeholder="Avis (optionnel)" onChange={(e) => { avis = e.target.value; }} />,
            okText: "Valider", cancelText: "Annuler",
            onOk: () => axiosInstance.put(`/api/commission-technique/dossiers/${d.codeId}/statuer`, { decision, avis })
                .then(() => { message.success("Décision enregistrée."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
        });
    };

    // Affectation d'un rapporteur (Pool) : parmi les participants Sinistre HORS compagnie d'origine.
    const affecter = (d) => {
        const candidats = participants.filter((p) => p.organismeCode !== d.organismeOrigineCode);
        if (!candidats.length) {
            message.info("Aucun participant éligible (hors compagnie d'origine). Paramétrez d'abord les participants.");
            return;
        }
        let userCodeId = null;
        Modal.confirm({
            title: `Affecter un rapporteur — ${d.numeroSinistre || ""}`,
            width: 520,
            content: (
                <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Le rapporteur ne peut pas appartenir à la compagnie d'origine ({d.organismeOrigineNom || d.organismeOrigineCode}).</Text>
                    <Select showSearch optionFilterProp="label" style={{ width: "100%", marginTop: 8 }} placeholder="Choisir un rapporteur"
                        onChange={(v) => { userCodeId = v; }}
                        options={candidats.map((p) => ({ value: p.userCodeId, label: `${p.userNom || p.userCodeId} — ${p.organismeNom || p.organismeCode}` }))} />
                </div>
            ),
            okText: "Affecter", cancelText: "Annuler",
            onOk: () => { if (!userCodeId) { message.warning("Sélectionnez un rapporteur."); return Promise.reject(); }
                return axiosInstance.put(`/api/commission-technique/dossiers/${d.codeId}/affecter/${userCodeId}`)
                    .then(() => { message.success("Rapporteur affecté."); charger(); })
                    .catch((e) => message.error(e?.response?.data?.message || "Échec.")); },
        });
    };

    // Autorisation directe (Pool) : approuve le règlement SANS séance.
    const autoriser = (d) => {
        let avis = "";
        Modal.confirm({
            title: `Autoriser sans commission — ${d.numeroSinistre || ""}`,
            icon: <ThunderboltOutlined style={{ color: "#faad14" }} />,
            content: (
                <div style={{ marginTop: 8 }}>
                    <Text>Le règlement sera autorisé directement par le Pool, sans passer par une séance.</Text>
                    <Input.TextArea rows={3} style={{ marginTop: 8 }} placeholder="Motif / avis (optionnel)" onChange={(e) => { avis = e.target.value; }} />
                </div>
            ),
            okText: "Autoriser", cancelText: "Annuler",
            onOk: () => axiosInstance.put(`/api/commission-technique/dossiers/${d.codeId}/autoriser`, { avis })
                .then(() => { message.success("Règlement autorisé (sans commission)."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
        });
    };

    const inscrire = (d) => {
        if (!d.affecteAUserCodeId) { message.warning("Affectez d'abord un rapporteur à ce dossier."); return; }
        const dispo = seances.filter((s) => s.statut !== "CLOTUREE");
        if (!dispo.length) { message.info("Créez d'abord une séance."); return; }
        let seanceCodeId = null;
        Modal.confirm({
            title: `Inscrire ${d.numeroSinistre || ""} à une séance`,
            content: (
                <Select style={{ width: "100%", marginTop: 8 }} placeholder="Choisir une séance"
                    onChange={(v) => { seanceCodeId = v; }}
                    options={dispo.map((s) => ({ value: s.codeId, label: `${s.numeroSeance} · ${s.dateSeance || ""}` }))} />
            ),
            okText: "Inscrire", cancelText: "Annuler",
            onOk: () => { if (!seanceCodeId) return Promise.reject();
                return axiosInstance.put(`/api/commission-technique/dossiers/${d.codeId}/inscrire/${seanceCodeId}`)
                    .then(() => { message.success("Dossier inscrit."); charger(); })
                    .catch((e) => message.error(e?.response?.data?.message || "Échec.")); },
        });
    };

    const dossierColumns = [
        { title: "N° sinistre", dataIndex: "numeroSinistre", render: (v) => v || "-" },
        { title: "Branche", dataIndex: "brancheCode", width: 80, render: (v) => <Tag>{v}</Tag> },
        { title: "Nature", dataIndex: "natureSinistre", width: 95, render: (v) => v ? <Tag color="purple">{NATURES.find((n) => n.code === v)?.label || v}</Tag> : <Text type="secondary">—</Text> },
        { title: "Compagnie d'origine", dataIndex: "organismeOrigineNom", render: (v, r) => v || r.organismeOrigineCode || "-" },
        { title: "Rapporteur", dataIndex: "affecteAUserNom", render: (v) => v ? <Tag color="geekblue">{v}</Tag> : <Text type="secondary">non affecté</Text> },
        { title: "Motif", dataIndex: "motif", width: 120, render: (v, r) => r.autoriseDirectement ? <Tag color="gold">Autorisé (Pool)</Tag> : v === "AUTO_SEUIL" ? <Tag color="volcano">Auto (seuil)</Tag> : <Tag>Manuel</Tag> },
        { title: "Déclencheur", dataIndex: "montantDeclencheur", align: "right", render: (v) => v ? `${fmt(v)} F` : "-" },
        { title: "Statut", dataIndex: "statut", width: 110, render: (v) => <Tag color={STATUT[v]?.color}>{STATUT[v]?.label || v}</Tag> },
        {
            title: "Actions", key: "act", width: 320, render: (_, d) => {
                const actif = d.statut !== "CLOTURE" && d.statut !== "APPROUVE";
                const estDeclencheur = d.motif === "AUTO_SEUIL" && !!d.decaissementCodeId;
                return (
                    <Space wrap>
                        <Button size="small" icon={<EyeOutlined />} onClick={() => ouvrirDossier(d)}>Ouvrir</Button>
                        {isPool() && actif && (
                            <Button size="small" icon={<SolutionOutlined />} onClick={() => affecter(d)}>
                                {d.affecteAUserCodeId ? "Réaffecter" : "Affecter"}
                            </Button>
                        )}
                        {isPool() && actif && estDeclencheur && (
                            <Button size="small" style={{ borderColor: "#faad14", color: "#d48806" }} icon={<ThunderboltOutlined />} onClick={() => autoriser(d)}>Autoriser</Button>
                        )}
                        {isMembre() && actif && (
                            <>
                                {!d.seance && <Button size="small" onClick={() => inscrire(d)}>Inscrire</Button>}
                                <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => statuer(d, "APPROUVE")} />
                                <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => statuer(d, "REJETE")} />
                                <Button size="small" icon={<ClockCircleOutlined />} onClick={() => statuer(d, "AJOURNE")} />
                            </>
                        )}
                    </Space>
                );
            },
        },
    ];

    const creerSeance = () => {
        let date = dayjs(); let objet = ""; let observations = "";
        Modal.confirm({
            title: "Nouvelle séance — Commission Technique Sinistre",
            content: (
                <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
                    <DatePicker style={{ width: "100%" }} defaultValue={date} format="DD/MM/YYYY" onChange={(d) => { date = d; }} />
                    <Input.TextArea rows={2} placeholder="Objet / ordre du jour" onChange={(e) => { objet = e.target.value; }} />
                    <Input.TextArea rows={3} placeholder="Observations de la séance (optionnel)" onChange={(e) => { observations = e.target.value; }} />
                </Space>
            ),
            okText: "Créer", cancelText: "Annuler",
            onOk: () => axiosInstance.post("/api/commission-technique/seances", { dateSeance: (date || dayjs()).format("YYYY-MM-DD"), objet, observations, type: TYPE })
                .then(() => { message.success("Séance créée."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
        });
    };
    const cloturer = (s) => Modal.confirm({
        title: `Clôturer la séance ${s.numeroSeance} ?`,
        content: "Les dossiers rattachés ne seront plus visibles par les autres compagnies.",
        okText: "Clôturer", okButtonProps: { danger: true }, cancelText: "Annuler",
        onOk: () => axiosInstance.put(`/api/commission-technique/seances/${s.codeId}/cloturer`)
            .then(() => { message.success("Séance clôturée."); charger(); })
            .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
    });
    const marquerTenue = (s) => axiosInstance.put(`/api/commission-technique/seances/${s.codeId}/tenue`)
        .then(() => { message.success("Séance marquée tenue."); charger(); })
        .catch((e) => message.error(e?.response?.data?.message || "Échec."));
    const editerSeance = (s) => {
        let objet = s.objet || ""; let observations = s.observations || "";
        Modal.confirm({
            title: `Séance ${s.numeroSeance} — objet & observations`,
            content: (
                <Space direction="vertical" style={{ width: "100%", marginTop: 8 }}>
                    <Input.TextArea rows={2} defaultValue={objet} placeholder="Objet" onChange={(e) => { objet = e.target.value; }} />
                    <Input.TextArea rows={4} defaultValue={observations} placeholder="Observations" onChange={(e) => { observations = e.target.value; }} />
                </Space>
            ),
            okText: "Enregistrer", cancelText: "Annuler",
            onOk: () => axiosInstance.put(`/api/commission-technique/seances/${s.codeId}`, { objet, observations })
                .then(() => { message.success("Séance mise à jour."); charger(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
        });
    };
    const telechargerPv = (chemin) => {
        const filename = (chemin || "").split("/").pop();
        axiosInstance.get(`/api/commission-technique/pv/${encodeURIComponent(filename)}`, { responseType: "blob" })
            .then((r) => {
                const url = window.URL.createObjectURL(new Blob([r.data]));
                const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch(() => message.error("Téléchargement impossible."));
    };

    const seanceColumns = [
        { title: "N° séance", dataIndex: "numeroSeance" },
        { title: "Date", dataIndex: "dateSeance", render: (v) => v ? dayjs(v).format("DD/MM/YYYY") : "-" },
        { title: "Objet", dataIndex: "objet", render: (v) => v || "-" },
        { title: "Observations", dataIndex: "observations", render: (v) => v ? <Text style={{ fontSize: 12 }}>{v}</Text> : <Text type="secondary">—</Text> },
        { title: "Statut", dataIndex: "statut", width: 100, render: (v) => <Tag color={SEANCE_STATUT[v]}>{v}</Tag> },
        {
            title: "PV / comptes rendus", dataIndex: "comptesRendus",
            render: (v) => (v || []).length
                ? <Space wrap>{(v || []).map((cr, i) => <Button key={i} size="small" type="link" style={{ padding: "0 4px" }} icon={<FileTextOutlined />} onClick={() => telechargerPv(cr)}>{cr.split("/").pop()}</Button>)}</Space>
                : <Text type="secondary">—</Text>,
        },
        {
            title: "Actions", key: "a", width: 340, render: (_, s) => isMembre() && s.statut !== "CLOTUREE" ? (
                <Space wrap>
                    <Button size="small" icon={<EditOutlined />} onClick={() => editerSeance(s)}>Éditer</Button>
                    {s.statut === "PLANIFIEE" && <Button size="small" icon={<CheckCircleOutlined />} onClick={() => marquerTenue(s)}>Tenue</Button>}
                    <Upload showUploadList={false} multiple
                        customRequest={({ file, onSuccess, onError }) => {
                            const fd = new FormData(); fd.append("fichiers", file);
                            axiosInstance.post(`/api/commission-technique/seances/${s.codeId}/comptes-rendus`, fd)
                                .then(() => { onSuccess?.(); message.success("PV joint."); charger(); })
                                .catch((e) => { onError?.(e); message.error("Échec de l'upload."); });
                        }}>
                        <Button size="small" icon={<UploadOutlined />}>Joindre PV</Button>
                    </Upload>
                    <Button size="small" danger onClick={() => cloturer(s)}>Clôturer</Button>
                </Space>
            ) : null,
        },
    ];

    const enCours = useMemo(() => dossiers.filter((d) => d.statut !== "CLOTURE").length, [dossiers]);
    const bloques = useMemo(() => dossiers.filter((d) => d.motif === "AUTO_SEUIL" && d.statut !== "APPROUVE" && d.statut !== "CLOTURE").length, [dossiers]);

    const enregistrerSeuil = (branche, nature, montant) => axiosInstance
        .put(`/api/commission-technique/seuils/${branche}/${nature}/${Math.round(montant || 0)}`)
        .then((r) => { setMatrice(r.data || []); message.success(`Seuil ${branche}/${NATURES.find((n) => n.code === nature)?.label} enregistré.`); })
        .catch((e) => message.error(e?.response?.data?.message || "Échec (droit HPA109 requis)."));

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper" style={{ background: "#f0f2f5" }}>
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <div style={{ background: `linear-gradient(135deg, ${PRIMARY} 0%, #3b3a8a 100%)`, borderRadius: 16, padding: "20px 24px", marginBottom: 16, color: "#fff" }}>
                                <Title level={3} style={{ color: "#fff", margin: 0 }}><PartitionOutlined /> Commission Technique Sinistre</Title>
                                <Text style={{ color: "rgba(255,255,255,0.85)" }}>Dossiers partagés inter-compagnies, affectation d'un rapporteur, séances, PV, décisions et seuils par nature.</Text>
                            </div>

                            <Row gutter={[12, 12]} style={{ marginBottom: 14 }}>
                                <Col xs={12} sm={8}><Card><Statistic title="Dossiers en cours" value={enCours} prefix={<PartitionOutlined />} valueStyle={{ color: PRIMARY }} /></Card></Col>
                                <Col xs={12} sm={8}><Card><Statistic title="Règlements bloqués" value={bloques} prefix={<LockOutlined />} valueStyle={{ color: "#cf1322" }} /></Card></Col>
                                <Col xs={24} sm={8}><Card><Statistic title="Séances" value={seances.length} prefix={<SafetyCertificateOutlined />} valueStyle={{ color: "#0891b2" }} /></Card></Col>
                            </Row>

                            <Card bordered={false} style={{ borderRadius: 12 }}>
                                <Tabs items={[
                                    {
                                        key: "dossiers", label: "Dossiers en commission",
                                        children: dossiers.length
                                            ? <Table rowKey="codeId" size="small" loading={loading} columns={dossierColumns} dataSource={dossiers} scroll={{ x: "max-content" }} pagination={{ pageSize: 15 }} />
                                            : <Empty description="Aucun dossier en commission" />,
                                    },
                                    {
                                        key: "seances", label: "Séances & PV",
                                        children: (
                                            <>
                                                {isMembre() && <div style={{ textAlign: "right", marginBottom: 10 }}><Button type="primary" icon={<PlusOutlined />} onClick={creerSeance}>Nouvelle séance</Button></div>}
                                                <Table rowKey="codeId" size="small" columns={seanceColumns} dataSource={seances} scroll={{ x: "max-content" }} pagination={{ pageSize: 10 }} />
                                            </>
                                        ),
                                    },
                                    {
                                        key: "participants", label: "Participants",
                                        children: <ParticipantsPanel participants={participants} compagnies={compagnies} onChange={charger} />,
                                    },
                                    {
                                        key: "seuils", label: "Seuils (branche × nature)",
                                        children: (
                                            <>
                                                <Table rowKey="branche" size="small" pagination={false} dataSource={matrice} scroll={{ x: "max-content" }}
                                                    columns={[
                                                        { title: "Branche", dataIndex: "branche", width: 90, render: (v) => <Tag color={PRIMARY}>{v}</Tag> },
                                                        { title: "Seuil branche (repli)", dataIndex: "brancheGlobal", align: "right", render: (v) => v ? `${fmt(v)} F` : <Text type="secondary">—</Text> },
                                                        ...NATURES.map((n) => ({
                                                            title: n.label, dataIndex: n.code, align: "right", width: 190,
                                                            render: (v, row) => <SeuilCell branche={row.branche} nature={n.code} valeur={v || 0} disabled={!canConfig()} onSave={enregistrerSeuil} />,
                                                        })),
                                                    ]} />
                                                <Text type="secondary" style={{ display: "block", marginTop: 10 }}>
                                                    Un règlement dépassant le seuil de sa (branche × nature) déclenche le passage automatique en commission et bloque sa validation.
                                                    Si une case nature est à 0, on retombe sur le « Seuil branche (repli) ». 0 partout = désactivé. (Configuration : HPA109 / Pool.)
                                                </Text>
                                            </>
                                        ),
                                    },
                                ]} />
                            </Card>
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </div>
    );
};

// Cellule seuil éditable (branche × nature).
const SeuilCell = ({ branche, nature, valeur, disabled, onSave }) => {
    const [v, setV] = useState(valeur);
    useEffect(() => { setV(valeur); }, [valeur]);
    return (
        <Space.Compact style={{ width: "100%" }}>
            <InputNumber style={{ width: "100%" }} min={0} step={100000} value={v} disabled={disabled}
                formatter={(x) => `${x}`.replace(/\B(?=(\d{3})+(?!\d))/g, " ")} parser={(x) => (x || "").replace(/\s/g, "")}
                onChange={setV} />
            <Button type="primary" disabled={disabled} onClick={() => onSave(branche, nature, v)}>OK</Button>
        </Space.Compact>
    );
};

// Panneau de paramétrage des participants (par compagnie). Le Pool voit/gère toutes les compagnies ;
// une compagnie ne gère que la sienne.
const ParticipantsPanel = ({ participants, compagnies, onChange }) => {
    const pool = isPool();
    const [org, setOrg] = useState(pool ? undefined : myOrgCode());
    const [users, setUsers] = useState([]);
    const [selUser, setSelUser] = useState(undefined);
    const [saving, setSaving] = useState(false);

    const chargerUsers = (code) => {
        if (!code) { setUsers([]); return; }
        axiosInstance.get(`/api/utilisateur/organisme/code/${code}`).then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
    };
    useEffect(() => { if (!pool) chargerUsers(myOrgCode()); }, [pool]);

    const ajouter = () => {
        if (!org || !selUser) { message.warning("Compagnie et utilisateur requis."); return; }
        setSaving(true);
        axiosInstance.post("/api/commission-technique/participants", { organisme: org, userCodeId: selUser, type: TYPE })
            .then(() => { message.success("Participant ajouté."); setSelUser(undefined); onChange && onChange(); })
            .catch((e) => message.error(e?.response?.data?.message || "Échec."))
            .finally(() => setSaving(false));
    };
    const retirer = (p) => axiosInstance.delete(`/api/commission-technique/participants/${p.codeId}`)
        .then(() => { message.success("Participant retiré."); onChange && onChange(); })
        .catch((e) => message.error(e?.response?.data?.message || "Échec."));

    const visibles = pool ? participants : participants.filter((p) => p.organismeCode === myOrgCode());

    return (
        <>
            <Card size="small" style={{ marginBottom: 12, background: "#fafbff" }}>
                <Space wrap align="end">
                    {pool && (
                        <div>
                            <div style={{ fontSize: 12, color: "#888" }}>Compagnie</div>
                            <Select showSearch optionFilterProp="label" style={{ width: 240 }} placeholder="Compagnie"
                                value={org} onChange={(v) => { setOrg(v); setSelUser(undefined); chargerUsers(v); }}
                                options={compagnies.map((c) => ({ value: c.code, label: c.raisonSociale || c.code }))} />
                        </div>
                    )}
                    <div>
                        <div style={{ fontSize: 12, color: "#888" }}>Utilisateur</div>
                        <Select showSearch optionFilterProp="label" style={{ width: 280 }} placeholder="Utilisateur"
                            value={selUser} onChange={setSelUser}
                            options={users.map((u) => ({ value: u.codeId, label: `${u.personne ? `${u.personne.nom || ""} ${u.personne.prenom || ""}`.trim() : u.username}` }))} />
                    </div>
                    <Button type="primary" icon={<UserAddOutlined />} loading={saving} onClick={ajouter}>Ajouter</Button>
                </Space>
                <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                    Participants autorisés aux séances de Commission Technique Sinistre. C'est dans cette liste que le Pool
                    choisit le rapporteur d'un dossier (hors compagnie d'origine).
                </Text>
            </Card>
            <Table rowKey="codeId" size="small" dataSource={visibles} pagination={{ pageSize: 15 }} scroll={{ x: "max-content" }}
                columns={[
                    { title: "Compagnie", dataIndex: "organismeNom", render: (v, r) => v || r.organismeCode || "-" },
                    { title: "Participant", dataIndex: "userNom", render: (v, r) => v || r.userCodeId },
                    {
                        title: "", key: "x", width: 60, render: (_, p) => (pool || p.organismeCode === myOrgCode()) ? (
                            <Popconfirm title="Retirer ce participant ?" onConfirm={() => retirer(p)} okText="Oui" cancelText="Non">
                                <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                            </Popconfirm>
                        ) : null,
                    },
                ]}
                locale={{ emptyText: <Empty description="Aucun participant" /> }} />
        </>
    );
};

export default CommissionTechnique;
